import { Order, SharedProps } from '@/types'
import { Head, router } from '@inertiajs/react'
import { Avatar, Badge, Card, Divider, Group, Stack, Table, Text, Title } from '@mantine/core'
import { IconArrowLeft, IconClock, IconUserQuestion } from '@tabler/icons-react'
import dayjs from 'dayjs'

type VersionEvent = 'create' | 'update' | 'destroy'

type VersionItemType = 'Order' | 'Article' | 'Service'

interface Version {
  id: number
  item_type: VersionItemType
  item_id: number
  item_label: string | null
  event: VersionEvent
  created_at: string
  whodunnit_name: string | null
  whodunnit_email: string | null
  changeset: Record<string, [unknown, unknown]>
}

const ITEM_TYPE_LABELS: Record<VersionItemType, string> = {
  Order: 'Commande',
  Article: 'Article',
  Service: 'Service',
}

interface ActivityShowProps extends SharedProps {
  order: Order
  versions: Version[]
}

const FIELD_LABELS: Record<string, string> = {
  article_name: 'Article',
  reference: 'Référence',
  customer_id: 'Client',
  created_by_id: 'Créé par',
  cashed_by_id: 'Encaissé par',
  collected_by_id: 'Collecté par',
  delivered_at: 'Livré le',
  delivered_by_id: 'Déposé par',
  received_by_id: 'Reçu par',
  sms_sent_by_id: 'SMS envoyé par',
  paid_at: 'Payé le',
  payment_method: 'Mode de paiement',
  received_at: 'Reçu le',
  dispatched_at: 'Dispatché le',
  sms_sent_at: 'SMS envoyé le',
  planned_date: 'Date prévue',
  atelier_accepted_at: 'Accepté par atelier le',
  service_name: 'Service',
  price: 'Prix',
  instructions: 'Instructions',
  completed_at: 'Terminé le',
  completed_by_id: 'Terminé par',
  verified_at: 'Vérifié le',
  verified_by_id: 'Vérifié par',
}

const EVENT_BADGES: Record<VersionEvent, { color: string; label: string }> = {
  create: { color: 'green', label: 'Créé' },
  update: { color: 'blue', label: 'Modifié' },
  destroy: { color: 'red', label: 'Supprimé' },
}

const formatValue = (field: string, value: unknown) => {
  if (value === null || value === undefined || value === '') return '—'
  if (field.endsWith('_at') && typeof value === 'string') return dayjs(value).format('DD/MM/YYYY HH:mm')
  if (field.endsWith('_date') && typeof value === 'string') return dayjs(value).format('DD/MM/YYYY')
  if (typeof value === 'boolean') return value ? 'Oui' : 'Non'
  return String(value)
}

const formatField = (field: string) => FIELD_LABELS[field] || field

export default function OrderActivityShow({ order, versions, current_organization }: ActivityShowProps) {
  if (!current_organization) return null

  const handleBack = () => router.visit(`/organizations/${current_organization.slug}/orders/${order.id}`)

  const reversed = [...versions].reverse()

  return (
    <>
      <Head title={`Activité - Commande #${order.id}`} />

      <Card withBorder radius="md" padding="lg" shadow="sm">
        <Stack gap="lg">
          <Group justify="space-between" align="center">
            <div>
              <Title>Activité - Commande #{order.id}</Title>
              <Text size="sm" c="dimmed">
                Historique des modifications
              </Text>
            </div>
            <Text component="a" onClick={handleBack} style={{ cursor: 'pointer' }} c="brand" size="sm">
              <Group gap={4}>
                <IconArrowLeft size={14} />
                Retour à la commande
              </Group>
            </Text>
          </Group>

          <Divider />

          {reversed.length === 0 ? (
            <Text c="dimmed">Aucune activité enregistrée pour cette commande.</Text>
          ) : (
            <Stack gap="md">
              {reversed.map((version) => (
                <ActivityEntry key={version.id} version={version} />
              ))}
            </Stack>
          )}
        </Stack>
      </Card>
    </>
  )
}

const initialsFor = (label: string) =>
  label
    .replace(/\(.*\)/, '')
    .trim()
    .split(/[\s.@]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || '?'

function AuthorBlock({ version }: { version: Version }) {
  const displayName = version.whodunnit_name || version.whodunnit_email

  if (!displayName) {
    return (
      <Group gap="xs" wrap="nowrap">
        <Avatar size="md" radius="xl" color="gray">
          <IconUserQuestion size={18} />
        </Avatar>
        <Stack gap={2}>
          <Text size="sm" fw={500} c="dimmed">
            Auteur inconnu
          </Text>
          <Group gap={4} wrap="nowrap">
            <IconClock size={12} color="var(--mantine-color-dimmed)" />
            <Text size="xs" c="dimmed">
              {dayjs(version.created_at).format('DD/MM/YYYY HH:mm')}
            </Text>
          </Group>
        </Stack>
      </Group>
    )
  }

  return (
    <Group gap="xs" wrap="nowrap">
      <Avatar size="md" radius="xl" color="blue">
        {initialsFor(displayName)}
      </Avatar>
      <Stack gap={2}>
        <Text size="sm" fw={600}>
          {displayName}
        </Text>
        <Group gap={4} wrap="nowrap">
          <IconClock size={12} color="var(--mantine-color-dimmed)" />
          <Text size="xs" c="dimmed">
            {dayjs(version.created_at).format('DD/MM/YYYY HH:mm')}
          </Text>
        </Group>
      </Stack>
    </Group>
  )
}

function ActivityEntry({ version }: { version: Version }) {
  const badge = EVENT_BADGES[version.event]
  const targetLabel = version.item_label || `${ITEM_TYPE_LABELS[version.item_type]} #${version.item_id}`
  const changedFields = Object.keys(version.changeset || {})

  return (
    <Card withBorder radius="sm" padding="md" data-testid="activity-entry">
      <Group justify="space-between" align="flex-start" mb="md" wrap="nowrap">
        <AuthorBlock version={version} />
        <Group gap="xs" wrap="nowrap">
          <Badge color={badge.color} variant="light">
            {badge.label}
          </Badge>
          <Text fw={600}>{targetLabel}</Text>
        </Group>
      </Group>

      {changedFields.length > 0 ? (
        <Table withTableBorder withColumnBorders fz="sm" style={{ tableLayout: 'fixed' }}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w="20%">Champ</Table.Th>
              <Table.Th w="40%">Avant</Table.Th>
              <Table.Th w="40%">Après</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {changedFields.map((field) => {
              const [previousValue, nextValue] = version.changeset[field]
              return (
                <Table.Tr key={field}>
                  <Table.Td>{formatField(field)}</Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {formatValue(field, previousValue)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{formatValue(field, nextValue)}</Text>
                  </Table.Td>
                </Table.Tr>
              )
            })}
          </Table.Tbody>
        </Table>
      ) : (
        <Text size="sm" c="dimmed">
          Aucun détail disponible pour cet événement.
        </Text>
      )}
    </Card>
  )
}
